import {
  AfterViewInit, Component, ElementRef, OnDestroy, QueryList, ViewChildren, computed, effect, inject, signal, untracked
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { MAX_NOTICES, Notice, NotifyKind, NotifyService } from '../../services/notify.service';

interface Look { icon: string; card: string; badge: string; text: string; detail: string; action: string; close: string; }

/**
 * Colours chosen by Heinrich (design canvas "App notifications", colours option 2, 12/10/2026): normal
 * notices white, warnings light yellow, errors light red with dark red text and an outlined action.
 */
const WHITE = {
  card: 'bg-white', text: 'text-gray-800', detail: 'text-gray-500',
  action: 'text-[var(--primary-dark)] hover:bg-[var(--app-hover)]', close: 'text-gray-500 hover:bg-[var(--app-hover)]'
};
const LOOK: Record<NotifyKind, Look> = {
  success: { ...WHITE, icon: 'check', badge: 'bg-[#ECFDF3] text-[#15803D]' },
  info: { ...WHITE, icon: 'info_outline', badge: 'bg-[var(--app-base)] text-gray-600' },
  warning: {
    icon: 'warning_amber', card: 'bg-[#FEF7C3] ring-1 ring-[#FDE272]', badge: 'bg-white text-[#A15C07]',
    text: 'text-[#713B12]', detail: 'text-[#713B12]', action: 'text-[#713B12] hover:bg-white/60', close: 'text-[#713B12] hover:bg-white/60'
  },
  error: {
    icon: 'priority_high', card: 'bg-[#FEE4E2] ring-1 ring-[#FDA29B]', badge: 'bg-white text-[#B42318]',
    text: 'text-[#7A271A]', detail: 'text-[#7A271A]',
    action: 'border border-[#B42318] bg-white text-[#B42318] hover:bg-[#FEF3F2]', close: 'text-[#7A271A] hover:bg-white/60'
  }
};

/** While the deck is closed each older notice peeks out this far below the one in front, and this much smaller. */
const PEEK_PX = 8;
const SHRINK = 0.05;
/** How many older notices peek out; any further back are hidden until the deck opens. */
const PEEKING = 2;
/** Between notices once the deck is open, and a notice's height until it has been measured. */
const GAP_PX = 8;
const UNMEASURED_PX = 46;
/** A focus this soon after a press came from the pointer, not the keyboard. */
const POINTER_FOCUS_MS = 500;
/** Longer than the leave animation, so a closed notice has left the page before focus is checked. */
const AFTER_LEAVE_MS = 400;

/**
 * NotifyService's notices at the top right under the profile, on every page (Heinrich, 13/10/2026), as a
 * deck: the newest in front and up to two older ones peeking out below it. Hovering the deck, or moving
 * into it with the keyboard, opens it into a list and holds every timer. Above dialogs, so an error raised
 * behind one is still seen.
 * A notice slides in and fades out (animate.enter / animate.leave: Angular keeps the element until the
 * leave animation ends), and does neither for people who ask for reduced motion. The keyframes move it
 * with `translate`, which adds to the deck's own `transform` instead of replacing it.
 */
@Component({
  selector: 'app-notify-stack',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notify-stack.component.html',
  styles: [`
    .notice > * { transition: opacity 150ms ease-out; }
    .behind { pointer-events: none; }
    .behind > * { opacity: 0; }
    .notify-in { animation: notify-in 180ms ease-out; }
    .notify-out { animation: notify-out 260ms ease-in forwards; }
    @keyframes notify-in { from { opacity: 0; translate: 0 -6px; } to { opacity: 1; translate: 0 0; } }
    @keyframes notify-out { from { opacity: 1; } to { opacity: 0; translate: 0 -4px; } }
    @media (prefers-reduced-motion: reduce) {
      .notify-in, .notify-out { animation-duration: 1ms; }
      .notice > * { transition: none; }
    }
  `]
})
export class NotifyStackComponent implements AfterViewInit, OnDestroy {
  readonly notify = inject(NotifyService);
  readonly max = MAX_NOTICES;
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @ViewChildren('card') private cards?: QueryList<ElementRef<HTMLElement>>;

  private readonly hovered = signal(false);
  private readonly focused = signal(false);
  /** Open into a list while the pointer is over the deck or the keyboard is in it. */
  readonly open = computed(() => this.hovered() || this.focused());

  /** Each notice's height by id, so the open list puts each one under the last. */
  private readonly heights = signal<ReadonlyMap<number, number>>(new Map());
  private readonly measured = new Map<number, HTMLElement>();
  private observer?: ResizeObserver;
  private cardsChanged?: Subscription;
  private pressedAt = 0;

  constructor() {
    // A notice closed under the pointer, or from the keyboard, takes its element with it, and the browser
    // does not always say the pointer or focus has left. Let go then, or the next notice's timer never runs.
    effect(() => {
      const count = this.notify.notices().length;
      untracked(() => {
        if (!count) {
          this.hovered.set(false);
          this.focused.set(false);
          this.hold();
        } else if (this.focused()) {
          setTimeout(() => this.checkFocus(), AFTER_LEAVE_MS);
        }
      });
    });
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined' || !this.cards) { return; }
    this.observer = new ResizeObserver(() => this.measure());
    this.watchCards();
    this.cardsChanged = this.cards.changes.subscribe(() => this.watchCards());
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.cardsChanged?.unsubscribe();
  }

  look(kind: NotifyKind): Look {
    return LOOK[kind];
  }

  trackById(_: number, notice: Notice): number {
    return notice.id;
  }

  /** Closed: the newest in front, the next two peeking out smaller below it. Open: one under another. */
  place(index: number): string {
    if (!this.open()) {
      const k = Math.min(index, PEEKING);
      return `translateY(${k * PEEK_PX}px) scale(${1 - k * SHRINK})`;
    }
    const list = this.notify.notices();
    let y = 0;
    for (let k = 0; k < index; k++) {
      y += (this.heights().get(list[k].id) ?? UNMEASURED_PX) + GAP_PX;
    }
    return `translateY(${y}px)`;
  }

  /** Peeking out behind the newest: its content hidden and out of the pointer's way. */
  behind(index: number): boolean {
    return !this.open() && index > 0;
  }

  /** Further back than the peeking ones: not shown until the deck opens. */
  hidden(index: number): boolean {
    return !this.open() && index > PEEKING;
  }

  hover(on: boolean): void {
    this.hovered.set(on);
    this.hold();
  }

  pressed(): void {
    this.pressedAt = Date.now();
  }

  focusIn(): void {
    if (Date.now() - this.pressedAt < POINTER_FOCUS_MS) { return; }   // a click focusing a button
    this.focused.set(true);
    this.hold();
  }

  focusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next && this.host.nativeElement.contains(next)) { return; }
    this.focused.set(false);
    this.hold();
  }

  /** Nothing closes by itself while the deck is open, so a notice is not gone while it is being read. */
  private hold(): void {
    if (this.open()) { this.notify.pause(); } else { this.notify.resume(); }
  }

  private checkFocus(): void {
    if (!this.focused() || this.host.nativeElement.contains(document.activeElement)) { return; }
    this.focused.set(false);
    this.hold();
  }

  /** Measure the notices on the page; stop measuring the ones that have gone. */
  private watchCards(): void {
    const now = new Map<number, HTMLElement>();
    this.cards?.forEach(ref => now.set(Number(ref.nativeElement.dataset['id']), ref.nativeElement));
    this.measured.forEach((card, id) => { if (!now.has(id)) { this.observer?.unobserve(card); } });
    now.forEach((card, id) => { if (!this.measured.has(id)) { this.observer?.observe(card); } });
    this.measured.clear();
    now.forEach((card, id) => this.measured.set(id, card));
  }

  private measure(): void {
    const next = new Map<number, number>();
    this.measured.forEach((card, id) => next.set(id, card.offsetHeight));
    this.heights.set(next);
  }
}
